import React, { useRef, useState } from 'react'
import {
  NativeSyntheticEvent,
  TextInputSubmitEditingEventData,
  View,
  TouchableOpacity,
  TextInput as RNTextInput,
  StyleSheet,
  StyleProp,
  ViewStyle,
} from 'react-native'
import { Controller, Control, FieldPath, FieldValues } from 'react-hook-form'
import { useTheme } from '@react-navigation/native'
import { TextInput } from '../molecules'
import { Icon, IconName } from '../atoms'
import { Text } from '../atoms'
import { Theme } from '@theme'
import { useLanguage } from '@context'
import { useCreateHashtag, usePaginateHashtags } from '@hooks'

type FormHashtagListProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>
  name: FieldPath<TFieldValues>
  values: string[]
  isGhost?: boolean
  placeholder?: string
  iconRight?: IconName
  style?: StyleProp<ViewStyle>
  withAutocomplete?: boolean
  // Mode "phrase" (objectifs) : chaque entrée est une phrase libre (sans #),
  // validée sur une fin de phrase (. ! ?) ou Entrée, et l'espace ne sépare pas.
  isSentence?: boolean
  // Mode "hashtag unique" (hobbies, compétences) : chaque entrée reste un seul
  // hashtag ; les mots d'une expression sont conservés (espaces -> underscores),
  // l'espace ne coupe donc pas l'entrée. Ex : "faire du karate" -> "#faire_du_karate".
  isSingleTag?: boolean
  addItem: (newValues: string[]) => void
  removeItem: (newValues: string[]) => void
}

// Séparateurs : hashtags = espace, virgule, point-virgule, point ;
// phrases = fins de phrase uniquement (l'espace fait partie de la phrase).
const HASHTAG_SEPARATOR_REGEX = /[\s,;.]+/
const SENTENCE_SEPARATOR_REGEX = /[.!?]+/

export const FormHashtagList = <TFieldValues extends FieldValues>({
  control,
  name,
  values = [], // Default to empty array
  isGhost,
  placeholder,
  iconRight,
  style,
  withAutocomplete,
  isSentence,
  isSingleTag,
  addItem,
  removeItem,
  contactId,
}: FormHashtagListProps<TFieldValues> & { contactId?: string }) => {
  const { t } = useLanguage()

  const theme = useTheme()
  const styles = getStyle(theme)

  const inputRef = useRef<RNTextInput>(null)
  const [inputValue, setInputValue] = useState('')
  const [showAutocomplete, setShowAutocomplete] = useState(false)

  const { flatData: hashtags } = usePaginateHashtags(inputValue)
  const { mutate: addHashtag } = useCreateHashtag()
  const addTag = (tag: string) => {
    const trimmed = tag.trim()
    if (!trimmed) return
    let value: string
    if (isSentence) {
      // Mode phrase : on conserve le texte tel quel (sans préfixe #).
      value = trimmed
    } else if (isSingleTag) {
      // Hashtag unique : on garde l'expression entière, les espaces internes
      // deviennent des underscores (ex : "faire du karate" -> "#faire_du_karate").
      const cleanTag = trimmed.replace(/^#+/, '').trim().replace(/\s+/g, '_')
      if (!cleanTag) return
      value = '#' + cleanTag
    } else {
      const cleanTag = trimmed.replace('#', '').trim()
      if (!cleanTag) return
      value = '#' + cleanTag
    }
    if (!values.includes(value)) {
      addItem([...values, value])
    }
  }

  const handleAutocompleteSelect = (hashtag: string) => {
    addTag(hashtag)
    setInputValue('')
    setShowAutocomplete(false)
  }

  // En mode phrase et en mode hashtag unique, l'espace ne coupe pas l'entrée :
  // on ne valide que sur une fin de phrase (. ! ?) ou Entrée.
  const separatorRegex =
    isSentence || isSingleTag ? SENTENCE_SEPARATOR_REGEX : HASHTAG_SEPARATOR_REGEX

  const handleTextChange = (text: string) => {
    // On valide dès qu'un séparateur est tapé : espace/virgule/point-virgule/point
    // pour les hashtags, fin de phrase (. ! ?) pour les objectifs.
    if (separatorRegex.test(text)) {
      text
        .split(separatorRegex)
        .filter((part) => part.trim())
        .forEach((part) => addTag(part))
      setInputValue('')
    } else {
      setInputValue(text)
    }
    // Pas d'autocomplétion en mode phrase ni en mode hashtag unique.
    setShowAutocomplete(!isSentence && !isSingleTag && text.length > 0)
  }

  const handleSubmitEditing = (e: NativeSyntheticEvent<TextInputSubmitEditingEventData>) => {
    const newItem = e.nativeEvent.text.trim()
    if (!newItem) return

    if (isSentence || isSingleTag) {
      // Objectif / hobby / compétence : on enregistre l'entrée entière comme un
      // seul chip, sans créer de hashtag global à partir d'une expression multi-mots.
      addTag(newItem)
      setInputValue('')
      return
    }

    // If hashtag exists, just add
    const existing = hashtags.find((h) => h.name.toLowerCase() === newItem.toLowerCase())
    if (existing) {
      addTag(existing.name)
    } else {
      // Create new hashtag and add contact if provided
      addHashtag({ name: '#' + newItem.trim().replace('#', ''), contactAccountIds: [] })
      addTag(newItem)
    }
    setInputValue('')
    setShowAutocomplete(false)
  }

  const handleRemoveItem = (indexToRemove: number) => {
    const newValue = values.filter((_, index) => index !== indexToRemove)
    removeItem(newValue)
  }

  return (
    <View style={styles.container}>
      <Controller
        control={control}
        name={name}
        render={({ fieldState: { error } }) => (
          <>
            <View
              style={[
                isGhost ? null : styles.inputContainer,
                !isGhost && error && { borderColor: theme.colors.error },
              ]}
            >
              {isGhost && (
                <Text type="bodySmall" color={theme.colors.greyBlue} style={styles.inputTitle}>
                  {name}
                </Text>
              )}
              <TextInput
                currentRef={inputRef}
                containerStyle={styles.input}
                style={style}
                value={inputValue}
                onChangeText={handleTextChange}
                onSubmitEditing={handleSubmitEditing}
                placeholder={placeholder || t('registerStepHastags.placeholder')}
                returnKeyType="done"
                noBorder={!isGhost}
                iconRight={iconRight}
              />
              {/* Autocomplete dropdown */}
              {withAutocomplete &&
                showAutocomplete &&
                Array.isArray(hashtags) &&
                hashtags.length > 0 && (
                  <View
                    style={{
                      backgroundColor: theme.colors.white,
                      borderWidth: 1,
                      borderColor: theme.colors.lightGrey,
                      borderRadius: 8,
                      marginTop: 4,
                      zIndex: 10,
                    }}
                  >
                    {hashtags.map((h) => (
                      <TouchableOpacity
                        key={h.id}
                        onPress={() => handleAutocompleteSelect(h.name)}
                        style={{ padding: 10 }}
                      >
                        <Text type="body">{h.name}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
              <View style={styles.tagsContainer}>
                {values?.map((interest, index) => (
                  <TouchableOpacity
                    key={index}
                    onPress={() => handleRemoveItem(index)}
                    style={styles.tagButton}
                  >
                    <Text color={theme.colors.primary} type="bodySmall">
                      {interest}
                    </Text>
                    <Icon name="cross" color={theme.colors.primary} size={14} />
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            {error && (
              <Text style={styles.errorMessage} type="microText" color={theme.colors.error}>
                {error.message}
              </Text>
            )}
          </>
        )}
      />
    </View>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
    },
    inputTitle: {
      marginBottom: theme.spacing.sm,
    },
    inputContainer: {
      flex: 1,
      backgroundColor: theme.colors.background,
      borderRadius: theme.border.large,
      borderWidth: 2,
      borderColor: theme.colors.lightGrey,
      padding: theme.spacing.lg,
      minHeight: 300,
    },
    input: {
      marginBottom: 0,
    },
    tagsContainer: {
      flexWrap: 'wrap',
      flexDirection: 'row',
      marginTop: theme.spacing.md,
    },
    tagButton: {
      flexDirection: 'row',
      alignItems: 'center',
      marginRight: theme.spacing.sm,
      marginBottom: theme.spacing.sm,
      borderWidth: 1,
      borderRadius: theme.border.extraLarge,
      borderColor: theme.colors.primary,
      paddingVertical: theme.spacing.xs,
      paddingHorizontal: theme.spacing.sm,
      backgroundColor: theme.colors.white,
    },
    errorMessage: {
      alignSelf: 'center',
      paddingVertical: theme.spacing.sm,
      color: theme.colors.error,
    },
  })
